import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Input, Spin, Pagination, TreeSelect } from 'antd'
import { useDispatch } from 'react-redux'
import { ArrowLeftOutlined, SearchOutlined, CaretUpOutlined, CaretDownOutlined, DownloadOutlined } from '@ant-design/icons'
import { COLORS } from 'theme'
import styled from 'styled-components'
import studentRequest from 'utils/studentRequest'
import { getAssessmentDegrees, getAssessmentDepartments, getAssessmentSpecializations } from '../../actions'
import StudentProgressDrawer from './components/StudentProgressDrawer'

import {
  SearchSection,
  ShowingText,
  TableTop,
  CustomStyledTable,
} from '../../Style/style'

const CandidateListWrapper = styled.div`
  padding: 12px;
  border-radius: 8px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
`

const HeaderFullWidth = styled.div`
  width: 100%;
  max-width: 100%;
`

const FilterSection = styled.div`
  margin-bottom: 0px;
`

const FilterInlineContainer = styled.div`
  padding: 24px 0;
  margin-bottom: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
`

const FilterGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
`

const FilterGroup = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;

  .ant-select,
  .ant-tree-select {
    border-radius: 8px;
    border: 1px solid #d1d5db;
    transition: all 0.2s ease;
    min-width: 160px;
    max-width: 180px;

    &:hover {
      border-color: #3249d7;
      box-shadow: 0 0 0 2px rgba(50, 73, 215, 0.1);
    }
  }
`

const StyledFormItem = styled.div`
  .ant-select {
    width: 100%;
  }
`

const EnhancedFilterLabel = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`

const StyledSearchInput = styled(Input)`
  height: 48px;
  border: none;
  box-shadow: none !important;

  &:focus,
  &:hover,
  &.ant-input-affix-wrapper-focused {
    border: none;
    box-shadow: none !important;
  }

  .ant-input {
    height: 100%;
    border: none;
    box-shadow: none !important;

    &:focus {
      box-shadow: none !important;
    }
  }
`

const AlignedTableTop = styled(TableTop)`
  margin-bottom: 0;

  ${SearchSection} {
    margin-bottom: 0;
  }

  ${ShowingText} {
    margin-bottom: 0;
  }
`

const NoData = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
`

const SortHeader = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 4px;

  &:hover {
    color: #1890ff;
  }
`

const SortIcons = styled.span`
  display: inline-flex;
  flex-direction: column;
  font-size: 10px;
  line-height: 1;
  .active {
    color: #1890ff;
  }
  .inactive {
    color: #bfbfbf;
  }
`

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #3249d7;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  height: 36px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #2a3eb8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const buildTreeData = (data, selectAllLabel = 'Select All') => {
  if (!data || data.length === 0) return []
  const validData = data.filter(item => {
    const name = item?.name || item?.label || ''
    const trimmedName = String(name).trim()
    return trimmedName && trimmedName !== '-' && trimmedName !== '---' && trimmedName.toLowerCase() !== 'null'
  })
  if (validData.length === 0) return []
  const children = validData.map(item => ({
    title: item.name || item.label,
    value: item.id || item.value,
    key: item.id || item.value,
  }))
  return [{
    title: selectAllLabel,
    value: 'all',
    key: 'all',
    children: children,
  }]
}

const extractIds = (values) => {
  if (!Array.isArray(values)) return []
  return values.map(v => v.value || v).filter(id => id !== 'all')
}

const SortIndicator = ({ field, currentSort }) => {
  const isActive = currentSort.field === field
  return (
    <SortIcons>
      <CaretUpOutlined className={isActive && currentSort.order === 'asc' ? 'active' : 'inactive'} />
      <CaretDownOutlined className={isActive && currentSort.order === 'desc' ? 'active' : 'inactive'} />
    </SortIcons>
  )
}

const TpoStudentListTable = ({
  instituteId,
  onBack,
  selectedYear,
}) => {
  const dispatch = useDispatch()
  const [students, setStudents] = useState([])
  const [pagination, setPagination] = useState({ pageNumber: 1, totalCount: 0, pageSize: 10 })
  const [loading, setLoading] = useState(false)
  const [localSearchTerm, setLocalSearchTerm] = useState('')
  const [searchText, setSearchText] = useState('')
  const [selectedStudent, setSelectedStudent] = useState(null)
  const debounceRef = useRef(null)

  const [sortBy, setSortBy] = useState({ field: 'name', order: 'asc' })

  const [degreeOptions, setDegreeOptions] = useState([])
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [specializationOptions, setSpecializationOptions] = useState([])

  const [topLevelDegree, setTopLevelDegree] = useState([])
  const [topLevelDepartment, setTopLevelDepartment] = useState([])
  const [topLevelSpecialization, setTopLevelSpecialization] = useState([])

  // Fetch filter options
  useEffect(() => {
    const fetchAllFilters = async () => {
      if (!instituteId) return
      try {
        const [degrees, departments, specializations] = await Promise.all([
          dispatch(getAssessmentDegrees({ instituteId, domains: [] })),
          dispatch(getAssessmentDepartments({ instituteId, degrees: [] })),
          dispatch(getAssessmentSpecializations({ instituteId, departments: [] })),
        ])
        setDegreeOptions(buildTreeData(degrees, 'Select All Degrees'))
        setDepartmentOptions(buildTreeData(departments, 'Select All Depts.'))
        setSpecializationOptions(buildTreeData(specializations, 'Select All Specs.'))
      } catch (error) {
        console.error('Error fetching filter options:', error)
      }
    }
    fetchAllFilters()
  }, [dispatch, instituteId, selectedYear])

  // Fetch student data
  const fetchData = useCallback(async (page = 1) => {
    if (!instituteId) return
    setLoading(true)
    try {
      const response = await studentRequest.post(`/students/assessments/tpo/student-list/${instituteId}`, {
        pageNumber: page,
        pageSize: 10,
        searchParams: searchText,
        passingYear: selectedYear,
        degree: extractIds(topLevelDegree),
        department: extractIds(topLevelDepartment),
        specialization: extractIds(topLevelSpecialization),
        sortBy,
      })
      const data = response?.data?.data
      setStudents(data?.students || data?.data || [])
      setPagination({
        pageNumber: data?.pageNumber || page,
        totalCount: data?.totalCount || 0,
        pageSize: data?.pageSize || 10,
      })
    } catch (error) {
      console.error('Error fetching student list:', error)
      setStudents([])
    } finally {
      setLoading(false)
    }
  }, [instituteId, selectedYear, searchText, topLevelDegree, topLevelDepartment, topLevelSpecialization, sortBy])

  useEffect(() => {
    fetchData(1)
  }, [instituteId, selectedYear, searchText, topLevelDegree, topLevelDepartment, topLevelSpecialization, sortBy])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleSearch = useCallback((e) => {
    const value = e.target.value
    setLocalSearchTerm(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearchText(value)
    }, 500)
  }, [])

  const handleSort = useCallback((field) => {
    setSortBy(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }, [])

  const [exporting, setExporting] = useState(false)
  const handleExport = async () => {
    if (!instituteId) return
    try {
      setExporting(true)
      const response = await studentRequest.post(`/assessment/exportTpoStudentList/${instituteId}`, {
        searchParams: searchText,
        passingYear: selectedYear,
        degree: extractIds(topLevelDegree),
        department: extractIds(topLevelDepartment),
        specialization: extractIds(topLevelSpecialization),
        sortBy,
      }, { responseType: 'blob' })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `student_list_${Date.now()}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setExporting(false)
    }
  }

  const columns = [
    {
      title: (
        <SortHeader onClick={() => handleSort('name')}>
          NAME & EMAIL
          <SortIndicator field="name" currentSort={sortBy} />
        </SortHeader>
      ),
      dataIndex: 'name',
      key: 'name',
      width: '280px',
      fixed: 'left',
      render: (text, record) => (
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedStudent(record)}
        >
          <div style={{ fontWeight: '500', color: '#3249D7' }}>{text}</div>
          <div style={{ color: '#888', fontSize: '12px' }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: 'DEGREE / DEPT',
      dataIndex: 'degree',
      key: 'degree',
      width: '250px',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: '500', color: '#000000' }}>{record.degree}</div>
          <div style={{ color: '#888', fontSize: '12px' }}>{record.department}</div>
        </div>
      ),
    },
    {
      title: 'COMM. LEVEL',
      dataIndex: 'communicationLevel',
      key: 'communicationLevel',
      width: '160px',
      render: (text, record) => (
        <div style={{ fontWeight: '500', color: '#000000' }}>
          {text}
          {record.commImprovement && (
            <span style={{ fontSize: '9px', color: '#52c41a', marginLeft: '3px', fontWeight: '600' }}>
              {record.commImprovement}
            </span>
          )}
        </div>
      )
    },
    {
      title: 'APTITUDE LEVEL',
      dataIndex: 'aptitudeLevel',
      key: 'aptitudeLevel',
      width: '160px',
      render: (text, record) => (
        <div style={{ fontWeight: '500', color: '#000000' }}>
          {text}
          {record.aptImprovement && (
            <span style={{ fontSize: '9px', color: '#52c41a', marginLeft: '3px', fontWeight: '600' }}>
              {record.aptImprovement}
            </span>
          )}
        </div>
      )
    },
    {
      title: (
        <SortHeader onClick={() => handleSort('communicationNPS')}>
          COMM. NPS (OUT OF 100)
          <SortIndicator field="communicationNPS" currentSort={sortBy} />
        </SortHeader>
      ),
      dataIndex: 'communicationNPS',
      key: 'communicationNPS',
      width: '130px',
      render: (value) => (
        <div style={{ fontWeight: '500', color: value != null ? '#000000' : '#bfbfbf' }}>
          {value != null ? value.toFixed(2) : '-'}
        </div>
      ),
    },
    {
      title: (
        <SortHeader onClick={() => handleSort('aptitudeNPS')}>
          APT. NPS (OUT OF 100)
          <SortIndicator field="aptitudeNPS" currentSort={sortBy} />
        </SortHeader>
      ),
      dataIndex: 'aptitudeNPS',
      key: 'aptitudeNPS',
      width: '130px',
      render: (value) => (
        <div style={{ fontWeight: '500', color: value != null ? '#000000' : '#bfbfbf' }}>
          {value != null ? value.toFixed(2) : '-'}
        </div>
      ),
    },
    {
      title: 'COMM. (TAKEN/SENT)',
      key: 'commAssessments',
      width: '180px',
      render: (text, record) => (
        <span style={{ fontWeight: '500', color: '#000000' }}>
          {record.commTaken} / {record.commSent}
        </span>
      ),
    },
    {
      title: 'APTITUDE (TAKEN/SENT)',
      key: 'aptAssessments',
      width: '180px',
      render: (text, record) => (
        <span style={{ fontWeight: '500', color: '#000000' }}>
          {record.aptTaken} / {record.aptSent}
        </span>
      ),
    },
  ]

  return (
    <CandidateListWrapper>
      <HeaderFullWidth>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <button
            onClick={onBack}
            style={{
              marginRight: 10,
              background: 'none',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ArrowLeftOutlined />
          </button>
          <span style={{ fontSize: 18, fontWeight: 600 }}>Total Candidates List</span>
        </div>

        <FilterSection>
          <FilterInlineContainer>
            <FilterGrid>
              <FilterGroup>
                <EnhancedFilterLabel>Degree</EnhancedFilterLabel>
                <StyledFormItem>
                  <TreeSelect
                    treeData={degreeOptions}
                    value={topLevelDegree}
                    onChange={setTopLevelDegree}
                    treeCheckable={true}
                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                    placeholder="Select Degree"
                    maxTagCount={0}
                    maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                    treeDefaultExpandAll={true}
                    allowClear
                  />
                </StyledFormItem>
              </FilterGroup>
              <FilterGroup>
                <EnhancedFilterLabel>Department</EnhancedFilterLabel>
                <StyledFormItem>
                  <TreeSelect
                    treeData={departmentOptions}
                    value={topLevelDepartment}
                    onChange={setTopLevelDepartment}
                    treeCheckable={true}
                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                    placeholder="Select Dept."
                    maxTagCount={0}
                    maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                    treeDefaultExpandAll={true}
                    allowClear
                  />
                </StyledFormItem>
              </FilterGroup>
              <FilterGroup>
                <EnhancedFilterLabel>Specialization</EnhancedFilterLabel>
                <StyledFormItem>
                  <TreeSelect
                    treeData={specializationOptions}
                    value={topLevelSpecialization}
                    onChange={setTopLevelSpecialization}
                    treeCheckable={true}
                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                    placeholder="Select Spec."
                    maxTagCount={0}
                    maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                    treeDefaultExpandAll={true}
                    allowClear
                  />
                </StyledFormItem>
              </FilterGroup>
            </FilterGrid>
          </FilterInlineContainer>
        </FilterSection>

        <AlignedTableTop>
          <SearchSection>
            <StyledSearchInput
              placeholder="Search by Name or Email"
              prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
              value={localSearchTerm}
              onChange={handleSearch}
            />
          </SearchSection>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <ShowingText>
              Showing {students?.length || 0} of{' '}
              {pagination?.totalCount || 0}
            </ShowingText>
            <ExportButton onClick={handleExport} disabled={exporting}>
              <DownloadOutlined />
              {exporting ? 'Exporting...' : 'Export'}
            </ExportButton>
          </div>
        </AlignedTableTop>

        <Spin spinning={loading}>
          <CustomStyledTable
            columns={columns}
            dataSource={students}
            pagination={false}
            rowKey="id"
            scroll={{ x: '1300px' }}
            bordered
            locale={{
              emptyText: <NoData>No candidates found</NoData>,
            }}
          />
        </Spin>

        {pagination?.totalCount > 10 && (
          <div
            style={{
              margin: '16px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Pagination
              current={pagination.pageNumber}
              total={pagination.totalCount}
              pageSize={pagination.pageSize}
              onChange={(page) => fetchData(page)}
            />
          </div>
        )}
      </HeaderFullWidth>

      <StudentProgressDrawer
        visible={!!selectedStudent}
        onClose={() => setSelectedStudent(null)}
        student={selectedStudent}
        instituteId={instituteId}
      />
    </CandidateListWrapper>
  )
}

export default TpoStudentListTable
